import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import { FormEvent, useState, useEffect } from 'react';
import { FloatingLabel, Form, Modal, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { useCalendarContext } from 'providers/CalendarProvider';
import { ADD_NEW_EVENT, SET_CALENDAR_STATE } from 'reducers/CalendarReducer';
import { Evento } from 'data/calendarEvents';
import usePhoenixForm from 'hooks/usePhoenixForm';
import dayjs from 'dayjs';
import { useAuth } from 'providers/AuthProvider';

import { Proceso } from 'data/project-management/procesos';
import { TipoDeMovimiento } from 'data/project-management/tipoDeMovimiento';

const CalendarAddNewEventModal = () => {
  const {
    openNewEventModal,
    selectedStartDate,
    selectedEndDate,
    selectedEvent, // <-- Extraemos selectedEvent del contexto
    calendarDispatch
  } = useCalendarContext();
  
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [loadedProcesos, setLoadedProcesos] = useState(false);
  const [tiposEvento, setTiposEvento] = useState<TipoDeMovimiento[]>([]);
  const [loadedTipos, setLoadedTipos] = useState(false);
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
  const isEditing = !!selectedEvent; // Bandera para saber si estamos editando

  const { formData, setFormData, onChange } = usePhoenixForm<Partial<Evento>>({
    start: selectedStartDate,
    end: selectedEndDate,
    descripcion: '',
    texto: '',
    proc: '',
    tipo_de_mto: '',
    lugar: '',
    modalidad: '', 
    usuario: user?.iniciales || ''
  });

  const fetchProcesos = async () => {
    if (loadedProcesos) return;
    try {
      const res = await axios.get(`${apiUrl}/procesos/procesoscjson`);
      setProcesos(res.data);
      setLoadedProcesos(true);
    } catch (err) {
      console.error('Error fetching procesos:', err);
    }
  };

  const fetchTiposEvento = async () => {
    if (loadedTipos) return;
    try {
      const res = await axios.get(`${apiUrl}/tMtos/eventos`);
      setTiposEvento(res.data);
      setLoadedTipos(true);
    } catch (err) {
      console.error('Error fetching tipos de evento:', err);
    }
  };

  const handleDependentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let currentProc = formData.proc;
    let currentTipo = formData.tipo_de_mto;
    let currentModalidad = formData.modalidad;

    if (name === 'proc') currentProc = value;
    if (name === 'tipo_de_mto') currentTipo = value;
    if (name === 'modalidad') currentModalidad = value;

    const procObj = procesos.find(p => p.PROC === currentProc);
    const procName = procObj ? (procObj.AUX8 || procObj.AUX7 || '') : '';

    const tipoObj = tiposEvento.find(t => t.id === currentTipo);
    const tipoName = tipoObj ? tipoObj.nombre : '';

    let modSigla = '';
    if (currentModalidad === 'Virtual') modSigla = '(V)';
    else if (currentModalidad === 'Presencial') modSigla = '(P)';
    else if (currentModalidad === 'Semi Presencial') modSigla = '(SP)';

    const partesFormateadas = [tipoName, modSigla, procName].filter(Boolean);
    const nuevaDescripcion = partesFormateadas.join(' ').trim();

    setFormData(prev => ({
      ...prev,
      [name]: value,
      descripcion: nuevaDescripcion
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.start) {
      alert('El horario de inicio es obligatorio.');
      return;
    }

    setIsSubmitting(true);

    const formattedStart = dayjs(formData.start).format('YYYY-MM-DDTHH:mm:ss');
    const formattedEnd = formData.end ? dayjs(formData.end).format('YYYY-MM-DDTHH:mm:ss') : undefined;

    const dataToSend = {
          ...formData,
          id: isEditing ? selectedEvent.id : undefined, // <-- CAMBIO AQUÍ
          start: formattedStart,
          end: formattedEnd,
        };

    try {
      if (isEditing) {
        // Petición de ACTUALIZACIÓN
        await axios.post(`${apiUrl}/eventos/update`, dataToSend);
        
        // Recargamos la página para asegurar que el calendario 
        // muestre los datos frescos de la BD y evitamos duplicados en la UI
        window.location.reload(); 
      } else {
        // Petición de CREACIÓN
        const response = await axios.post(`${apiUrl}/eventos`, dataToSend);
        const newEvento = response.data;

        const calendarEvent = {
          ...newEvento,
          id: String(newEvento.id),
          title: newEvento.descripcion || 'Sin título',
          start: formattedStart, 
          end: formattedEnd,
          className: 'text-primary',
          extendedProps: newEvento
        };

        calendarDispatch({
          type: ADD_NEW_EVENT,
          payload: calendarEvent
        });

        handleClose();
      }
    } catch (error) {
      console.error('Error al guardar el evento:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    calendarDispatch({
      type: SET_CALENDAR_STATE,
      // Ponemos selectedEvent en null para asegurar que se cierren ambos modales a la vez
      payload: { openNewEventModal: false, selectedEvent: null } 
    });
  };

  useEffect(() => {
    if (openNewEventModal) {
      if (selectedEvent) {
        // ESTAMOS EDITANDO: Rellenar con los datos existentes
        const evt = selectedEvent.extendedProps;
        setFormData({
          id: selectedEvent.id, // <-- CAMBIO AQUÍ
          start: selectedEvent.start || undefined, // Objeto Date que trae FullCalendar
          end: selectedEvent.end || undefined, // Objeto Date que trae FullCalendar
          descripcion: evt.descripcion || '',
          texto: evt.texto || '',
          proc: evt.proc || '',
          tipo_de_mto: evt.tipo_de_mto || '',
          lugar: evt.lugar || '',
          modalidad: evt.modalidad || '', 
          usuario: evt.usuario || user?.iniciales || ''
        });
        
        // Disparamos la carga de opciones para que los selects muestren los nombres
        fetchProcesos();
        fetchTiposEvento();
      } else {
        // NUEVO EVENTO: Rellenar en blanco
        setFormData({
          start: selectedStartDate,
          end: selectedEndDate || (selectedStartDate ? dayjs(selectedStartDate).add(1, 'hour').toDate() : undefined),
          descripcion: '',
          texto: '',
          proc: '',
          tipo_de_mto: '',
          lugar: '',
          modalidad: '', 
          usuario: user?.iniciales || ''
        });
      }
    }

  }, [openNewEventModal, selectedEvent, selectedStartDate, selectedEndDate, user]);

  return (
    <Modal show={openNewEventModal} contentClassName="border-translucent" onHide={handleClose}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header className="px-card border-0">
          <div className="w-100 d-flex justify-content-between align-items-start">
            {/* Título dinámico */}
            <div><h5 className="mb-0 lh-sm text-body-highlight">{isEditing ? 'Editar Evento' : 'Nuevo Evento'}</h5></div>
            <Button className="p-1 fs-10 text-body" onClick={handleClose}>DESCARTAR</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="p-card py-0">
          
          <FloatingLabel controlId="descripcion" label="Nombre" className="mb-3">
            <input 
              id="descripcion" 
              className="form-control" 
              onChange={onChange} 
              name="descripcion" 
              type="text" 
              placeholder="Título" 
              required 
              value={formData.descripcion || ''} 
            />
          </FloatingLabel>

          <Row className="g-3 mb-3">
            <Col sm={6}>
              <FloatingLabel controlId="proc" label="Proceso (Proc)">
                <Form.Select 
                  id="proc" 
                  onChange={handleDependentChange} 
                  name="proc" 
                  value={formData.proc || ''} 
                  onFocus={fetchProcesos}
                >
                  <option value="">Seleccionar</option>
                  {procesos.map(p => (
                    <option key={p.PROC} value={p.PROC}>{p.AUX8 || p.AUX7}</option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col sm={6}>
              <FloatingLabel controlId="tipo_de_mto" label="Tipo de Movimiento">
                <Form.Select 
                  id="tipo_de_mto"
                  onChange={handleDependentChange}
                  name="tipo_de_mto"
                  value={formData.tipo_de_mto || ''}
                  onFocus={fetchTiposEvento}
                >
                  <option value="">Seleccionar</option>
                  {tiposEvento.map(t => (
                    <option key={t.id} value={t.id}>{t.nombre}</option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col sm={6}>
              <DatePicker
                value={formData.start} 
                options={{ enableTime: true, dateFormat: 'Y-m-d H:i' }}
                onChange={([date]) => {
                  if (date) {
                    const newEndDate = dayjs(date).add(1, 'hour').toDate();
                    setFormData({
                      ...formData,
                      start: date,
                      end: newEndDate
                    });
                  }
                }}
                render={(_, ref) => (
                  <Form.Floating>
                    <Form.Control type="text" placeholder="Inicia" ref={ref} id="startDatepicker" required />
                    <label htmlFor="startDatepicker" className="ps-6">Inicia</label>
                  </Form.Floating>
                )}
              />
            </Col>
            <Col sm={6}>
              <DatePicker
                value={formData.end}
                options={{ enableTime: true, dateFormat: 'Y-m-d H:i' }}
                onChange={([date]) => setFormData({ ...formData, end: date })}
                render={(_, ref) => (
                  <Form.Floating>
                    <Form.Control type="text" placeholder="Termina" ref={ref} id="endDatepicker" />
                    <label htmlFor="endDatepicker" className="ps-6">Termina</label>
                  </Form.Floating>
                )}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col sm={6}>
              <FloatingLabel controlId="lugar" label="Lugar">
                <input id="lugar" className="form-control" onChange={onChange} name="lugar" type="text" placeholder="Lugar" value={formData.lugar || ''} />
              </FloatingLabel>
            </Col>
            <Col sm={6}>
              <FloatingLabel controlId="modalidad" label="Modalidad">
                <Form.Select 
                  id="modalidad"
                  name="modalidad"
                  value={formData.modalidad || ''}
                  onChange={handleDependentChange}
                >
                  <option value="">Seleccionar (Opcional)</option>
                  <option value="Virtual">Virtual</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Semi Presencial">Semi Presencial</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
          </Row>

          <div className="py-2">
            <FloatingLabel controlId="texto" label="Texto Detallado">
              <textarea id="texto" className="form-control" onChange={onChange} placeholder="Texto Detallado" name="texto" style={{ height: '100px' }} value={formData.texto || ''} />
            </FloatingLabel>
          </div>

        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end align-items-center border-0">
          <Button variant="primary" type="submit" className="px-4" disabled={isSubmitting || !formData.start}>
            {/* Texto del botón dinámico */}
            {isSubmitting ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CalendarAddNewEventModal;